import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Empty, Spinner } from "@/components/UIKit";
import { AvtaleGiroCard, EFakturaCard } from "@/components/PaymentAgreements";
import { useAuth } from "@/lib/auth";
import { fmtNOK, fmtDate } from "@/lib/format";
import { Link } from "react-router-dom";
import { AlertCircle, TrendingUp, Upload, Loader2, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Betaling() {
  const { tenant } = useAuth();
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [ocrList, setOcrList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [registering, setRegistering] = useState(false);

  const load = async () => {
    const [o, p, of] = await Promise.all([
      api.get("/payments/outstanding"),
      api.get("/payments?limit=50"),
      api.get("/ocr"),
    ]);
    setData(o.data); setPayments(p.data.items); setOcrList(of.data);
  };
  useEffect(() => { load(); }, []);

  const uploadOcr = async (file) => {
    setUploading(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/ocr/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`${data.matched}/${data.total} betalinger matchet`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke tolke filen");
    } finally { setUploading(false); }
  };

  const registerMatched = async () => {
    if (!preview) return;
    setRegistering(true);
    try {
      const { data } = await api.post(`/ocr/${preview.ocr_file_id}/register`);
      toast.success(`${data.registered} innbetalinger registrert`);
      setPreview(null); load();
    } catch (e) { toast.error("Feilet"); }
    finally { setRegistering(false); }
  };

  return (
    <div data-testid="betaling-page">
      <PageHeader title="Betaling" subtitle="Utestående, OCR-avstemming og innbetalinger" />
      {tenant?.invoicing_via !== "tripletex" && <><AvtaleGiroCard /><EFakturaCard /></>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Card className="p-5" testid="kpi-outstanding-sum">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5 text-amber-400" /> Utestående
          </div>
          <div className="font-heading text-3xl tabular-nums text-amber-400">{data ? fmtNOK(data.total_outstanding_nok) : "—"}</div>
          <div className="text-xs text-zinc-500 mt-1">{data?.count || 0} åpne fakturaer</div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-400" /> Innbetalinger
          </div>
          <div className="font-heading text-3xl tabular-nums">{payments.length}</div>
          <div className="text-xs text-zinc-500 mt-1">registrert</div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">OCR-filer</div>
          <div className="font-heading text-3xl tabular-nums">{ocrList.length}</div>
          <div className="text-xs text-zinc-500 mt-1">opplastet</div>
        </Card>
      </div>

      {/* OCR opplasting */}
      <Card className="p-5 mb-4" testid="ocr-upload-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-heading font-medium text-zinc-100">Automatisk avstemming (bank OCR)</div>
            <div className="text-xs text-zinc-500 mt-0.5">Last opp CSV eller NETS OCR-fil. KID matches automatisk mot åpne fakturaer.</div>
          </div>
          <label className="h-9 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-2 cursor-pointer">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Behandler…" : "Last opp fil"}
            <input type="file" accept=".csv,.txt,.ocr" className="hidden" data-testid="ocr-file-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadOcr(f); e.target.value = ""; }} />
          </label>
        </div>
        <div className="text-xs text-zinc-500">
          <span className="text-zinc-400">CSV-format:</span> <code className="bg-zinc-900 px-1.5 py-0.5 rounded">dato;kid;beløp;referanse</code> — semikolon-separert, norsk tallformat (1&nbsp;234,56)
        </div>

        {preview && (
          <div className="mt-4 border-t border-zinc-900 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm">
                <span className="text-zinc-100 font-medium">{preview.total}</span> oppføringer ·
                <span className="text-emerald-400"> {preview.matched} matchet</span> ·
                <span className="text-amber-400"> {preview.unmatched} umatchet</span>
              </div>
              <button onClick={registerMatched} disabled={registering || preview.matched === 0}
                className="h-9 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black text-sm font-medium flex items-center gap-2" data-testid="ocr-register-btn">
                {registering ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Registrer {preview.matched} betalinger
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto border border-zinc-900 rounded">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-zinc-950/90 backdrop-blur">
                  <tr className="text-left text-[10px] uppercase text-zinc-500">
                    <th className="py-1.5 px-3 font-medium">KID</th>
                    <th className="py-1.5 px-3 font-medium text-right">Beløp</th>
                    <th className="py-1.5 px-3 font-medium">Status</th>
                    <th className="py-1.5 px-3 font-medium">Merknad</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.entries.map((e, i) => (
                    <tr key={i} className="border-t border-zinc-900">
                      <td className="py-1 px-3 font-mono text-emerald-400">{e.kid || "—"}</td>
                      <td className="py-1 px-3 tabular-nums text-right">{fmtNOK(e.amount_nok, { decimals: 2 })}</td>
                      <td className="py-1 px-3">
                        {e.status === "matched" ? <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Matchet</span>
                          : e.status === "duplicate" ? <span className="text-amber-400">Duplikat</span>
                          : <span className="text-zinc-500 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> Umatchet</span>}
                      </td>
                      <td className="py-1 px-3 text-zinc-500">{e.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-zinc-900 font-heading font-medium">Utestående fakturaer</div>
          {!data ? <div className="p-6"><Spinner /></div>
            : data.items.length === 0 ? <Empty title="Ingen utestående" subtitle="Alle fakturaer er betalt" />
            : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60">
                  <th className="py-2 px-4 font-medium">Fakturanr</th>
                  <th className="py-2 px-4 font-medium">Forfall</th>
                  <th className="py-2 px-4 font-medium text-right">Utestående</th>
                </tr>
              </thead>
              <tbody>
                {data.items.slice(0, 20).map((i) => (
                  <tr key={i.id} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                    <td className="py-2 px-4"><Link to={`/fakturering/${i.id}`} className="text-emerald-400 font-mono text-xs">{i.invoice_number}</Link></td>
                    <td className="py-2 px-4 text-zinc-400 text-xs">{fmtDate(i.due_date)}</td>
                    <td className="py-2 px-4 tabular-nums text-right text-amber-400">{fmtNOK((i.total_nok || 0) - (i.paid_nok || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="overflow-hidden">
          <div className="p-4 border-b border-zinc-900 font-heading font-medium">Siste innbetalinger</div>
          {payments.length === 0 ? <Empty title="Ingen innbetalinger" /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60">
                  <th className="py-2 px-4 font-medium">Dato</th>
                  <th className="py-2 px-4 font-medium">Metode</th>
                  <th className="py-2 px-4 font-medium">Referanse</th>
                  <th className="py-2 px-4 font-medium text-right">Beløp</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-zinc-900">
                    <td className="py-2 px-4 text-zinc-400 text-xs">{fmtDate(p.paid_at)}</td>
                    <td className="py-2 px-4 text-zinc-300">{p.method}</td>
                    <td className="py-2 px-4 text-zinc-500 text-xs">{p.reference || "—"}</td>
                    <td className="py-2 px-4 tabular-nums text-right text-emerald-400">{fmtNOK(p.amount_nok)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
